import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { Tag } from "../components/Tag";
import { ChangeSummary } from "../components/ChangeSummary";
import { isNewJob, isRecentlyClosedJob } from "../lib/tags";
import { PIPELINE_STATUSES, PRIORITIES, type Company, type Job, type Priority } from "../lib/types";

export function CompaniesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filter, setFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    priority: "High" as Priority,
    hq_location: "",
    company_type: "Public",
    ticker: "",
    employees: "",
    notes: "",
    careers_url: "",
  });

  const load = useCallback(async () => {
    if (!user) return;
    const [cRes, jRes] = await Promise.all([
      supabase.from("jh_companies").select("*").eq("user_id", user.id).order("name"),
      supabase.from("jh_jobs").select("*").eq("user_id", user.id),
    ]);
    if (cRes.error) setError(cRes.error.message);
    else setCompanies((cRes.data ?? []) as Company[]);
    if (jRes.error) setError(jRes.error.message);
    else setJobs((jRes.data ?? []) as Job[]);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const order: Record<Priority, number> = { Top: 0, High: 1, Medium: 2 };
    return companies
      .filter((c) => {
        if (!q) return true;
        return (
          c.name.toLowerCase().includes(q) ||
          c.hq_location.toLowerCase().includes(q) ||
          c.ticker.toLowerCase().includes(q) ||
          c.company_type.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => order[a.priority] - order[b.priority] || a.name.localeCompare(b.name));
  }, [companies, filter]);

  async function updatePriority(id: string, priority: Priority) {
    const { error: err } = await supabase
      .from("jh_companies")
      .update({ priority, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (err) setError(err.message);
    else setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, priority } : c)));
  }

  async function addCompany() {
    if (!user || !form.name.trim()) return;
    const external_id = `co_${form.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
    const { error: err } = await supabase.from("jh_companies").insert({
      user_id: user.id,
      external_id,
      priority: form.priority,
      name: form.name.trim(),
      hq_location: form.hq_location.trim(),
      company_type: form.company_type,
      ticker: form.ticker.trim().toUpperCase(),
      employees: form.employees.trim(),
      notes: form.notes.trim(),
      careers_url: form.careers_url.trim(),
    });
    if (err) setError(err.message);
    else {
      setShowForm(false);
      setForm({
        name: "",
        priority: "High",
        hq_location: "",
        company_type: "Public",
        ticker: "",
        employees: "",
        notes: "",
        careers_url: "",
      });
      await load();
    }
  }

  async function removeCompany(id: string) {
    if (!confirm("Remove this company and its jobs?")) return;
    const { error: err } = await supabase.from("jh_companies").delete().eq("id", id);
    if (err) setError(err.message);
    else await load();
  }

  function openJobs(companyId: string) {
    navigate(`/jobs?company=${companyId}`);
  }

  return (
    <div className="stack-gap">
      <ChangeSummary jobs={jobs} />
      <div className="stats">
        <div className="stat">
          <strong>{companies.length}</strong>
          <span>Companies watching</span>
        </div>
        <div className="stat">
          <strong>{jobs.filter((j) => !j.closed_at && j.status !== "Withdrawn").length}</strong>
          <span>Open jobs</span>
        </div>
        <div className="stat">
          <strong>{jobs.filter((j) => j.applied).length}</strong>
          <span>Applications submitted</span>
        </div>
        <div className="stat">
          <strong>
            {
              jobs.filter(
                (j) =>
                  j.applied &&
                  PIPELINE_STATUSES.includes(j.status) && j.status !== "Offer"
              ).length
            }
          </strong>
          <span>Active in pipeline</span>
        </div>
      </div>

      <div className="panel">
        <div className="toolbar">
          <input
            placeholder="Filter companies…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button type="button" className="btn primary" onClick={() => setShowForm((v) => !v)}>
            {showForm ? "Cancel" : "Add company"}
          </button>
        </div>
        {error ? <p className="error">{error}</p> : null}
        {showForm ? (
          <div className="stack-gap" style={{ marginBottom: 16 }}>
            <div className="field">
              <label>Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="field">
              <label>Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value as Priority })}
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>HQ location</label>
              <input
                value={form.hq_location}
                onChange={(e) => setForm({ ...form, hq_location: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Notes</label>
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="field">
              <label>Careers URL</label>
              <input
                value={form.careers_url}
                onChange={(e) => setForm({ ...form, careers_url: e.target.value })}
              />
            </div>
            <button type="button" className="btn primary" onClick={() => void addCompany()}>
              Save company
            </button>
          </div>
        ) : null}

        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Company</th>
                <th>HQ</th>
                <th>Type</th>
                <th>Ticker</th>
                <th>Notes</th>
                <th>Jobs / Apps</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const companyJobs = jobs.filter((j) => j.company_id === c.id);
                const appCount = companyJobs.filter((j) => j.applied).length;
                return (
                  <tr key={c.id}>
                    <td>
                      <select
                        value={c.priority}
                        onChange={(e) => void updatePriority(c.id, e.target.value as Priority)}
                        aria-label={`Priority for ${c.name}`}
                      >
                        {PRIORITIES.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <strong>{c.name}</strong>
                      {companyJobs.some(isNewJob) ? <Tag kind="new" /> : null}
                      {companyJobs.some(isRecentlyClosedJob) ? <Tag kind="closed" /> : null}
                    </td>
                    <td>{c.hq_location || "—"}</td>
                    <td>{c.company_type}</td>
                    <td>{c.ticker || "—"}</td>
                    <td>{c.notes || "—"}</td>
                    <td>
                      <button type="button" className="btn" onClick={() => openJobs(c.id)}>
                        {companyJobs.length} Job{companyJobs.length === 1 ? "" : "s"}, {appCount} App
                        {appCount === 1 ? "" : "s"}
                      </button>
                    </td>
                    <td>
                      <button type="button" className="btn danger" onClick={() => void removeCompany(c.id)}>
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
